import Vue from "vue";
import router from "@/router";

const getDefaultState = () => {
	return {
		users: [],
		pendingUsers: [],
		selectedUser: null,
		members: null,
		upgradeToPaidPlan: false,
		channelToken: null,
		usersOnline: {},
		organization: {
			id: null,
			purchases: [],
		},
	};
};

const Organizations = {
	namespaced: true,

	state: getDefaultState(),
	mutations: {
		SET_USERS: (state, users) => {
			state.users = users;
		},

		SET_PENDING_USERS: (state, payload) => {
			payload.forEach(user => {
				user.resending = false;
				user.resent = false;
				user.deleting = false;
			});

			state.pendingUsers = payload;
		},

		SET_SELECTED_USER: async (state, selectedUser) => {
			state.selectedUser = selectedUser;
		},

		SET_MEMBERS: (state, members) => {
			state.members = members;
		},
		SET_CHANNEL_TOKEN: (state, payload) => {
			state.channelToken = payload;
		},
		RESET_STATE: state => {
			Object.assign(state, getDefaultState());
		},
		USER_ROLE_UPDATED: () => {
		},
		USER_SUSPENDED: () => {
		},
		USER_UNSUSPENDED: () => {
		},
		UPGRADE_TO_PAID_PLAN: (state, payload) => {
			state.upgradeToPaidPlan = payload;
		},
		UPDATE_PENDING_USER_STATUS: (state, payload) => {
			let user = state.pendingUsers.find(user => user.id == payload.id);
			user[payload.key] = payload.value;
		},
		SET_USER_ONLINE: (state, payload) => {
			Vue.set(state.usersOnline, payload.toString(), true)
		},
		SET_USER_OFFLINE: (state, payload) => {
			Vue.set(state.usersOnline, payload.toString(), false)
		},
		SET_ORGANIZATION: (state, payload) => {
			state.organization = payload;
		},
	},
	actions: {
		inviteUsers: ({ commit, rootState }, payload) => {
			Vue.prototype.$apiService.post(`accounts/${rootState.User.account.id}/create`, payload);
		},

		fetchUsers: ({ commit, rootState }) => {
			if (rootState.User.account.role_id > 4) return;

			Vue.prototype.$apiService
				.get(`accounts/${rootState.User.account.id}/users`)
				.then(response => {
					commit("SET_USERS", response.data);

					if (rootState.Organizations.selectedUser) {
						const newSelectedUser = response.data.find(user => user.id === rootState.Organizations.selectedUser.id);

						commit("SET_SELECTED_USER", newSelectedUser);
					}
				})
				.catch(err => console.log(err.response));
		},
		fetchActiveUsers: ({ commit, rootState }) => {
			Vue.prototype.$apiService
				.get(`accounts/${rootState.User.account.id}/users`, {
					params: { accepted: 1 },
				})
				.then(response => {
					commit("SET_USERS", response.data);
				})
				.catch(err => console.log(err.response));
		},
		fetchPendingUsers: ({ commit, rootState, rootGetters }) => {
			if (rootGetters["User/isMemberOrLower"]) return;

			return Vue.prototype.$apiService
				.get(`accounts/${rootState.User.account.id}/users`, {
					params: { accepted: 0 },
				})
				.then(response => {
					commit("SET_PENDING_USERS", response.data);
					return response.data;
				})
				.catch(err => console.log(err.response));
		},
		fetch: ({ commit, rootState }) => {
			return Vue.prototype.$apiService
				.get(`accounts/${rootState.User.account.id}/organizations/${rootState.User.account.organization.id}`)
				.then(response => {
					commit("SET_ORGANIZATION", response.data);
					commit("SET_MEMBERS", response.data.members);
					commit("SET_CHANNEL_TOKEN", response.data.channel_token);
				})
				.catch(err => console.log(err.response));
		},
		updateUserRole: ({ commit, rootState }, payload) => {
			return Vue.prototype.$apiService
				.patch(`accounts/${rootState.User.account.id}/update/${payload.id}`, {
					role_id: payload.role_id,
				})
				.then(response => {
					commit("USER_ROLE_UPDATED", response.data);
				})
				.catch(err => console.log(err.response));
		},
		suspendUser: ({ commit, rootState }, suspendId) => {
			return Vue.prototype.$apiService
				.patch(`accounts/${rootState.User.account.id}/suspend/${suspendId}`, {
					suspend: 1,
				})
				.then(response => {
					commit("USER_SUSPENDED", response.data);
				})
				.catch(err => console.log(err.response));
		},
		unsuspendUser: ({ commit, rootState }, suspendId) => {
			return Vue.prototype.$apiService
				.patch(`accounts/${rootState.User.account.id}/suspend/${suspendId}`, {
					suspend: 0,
				})
				.then(response => {
					commit("USER_UNSUSPENDED", response.data);
				})
				.catch(err => console.log(err.response));
		},
		resetState: ({ commit }) => {
			commit("RESET_STATE");
		},

		upgradeToPaidPlan: ({ commit }, payload) => {
			localStorage.setItem('upgradeToPaidPlan', payload);
			commit("UPGRADE_TO_PAID_PLAN", payload);
		},

		finalizeUpgradeFromFreePlan: async ({ rootState, dispatch }, payload) => {
			localStorage.setItem('finalizeUpgradeToPaidPlan', localStorage.getItem('upgradeToPaidPlan'));
			localStorage.removeItem('upgradeToPaidPlan');

			dispatch("Loader/onUpgradePlan", null, { root: true });

			router.push({
				name: "account.billing",
			});
		},

		changePlan: ({ rootState }, payload) => {
			return Vue.prototype.$apiService.patch(`accounts/${rootState.User.account.id}/change-plan`, {
				plan_id: payload,
			});
		},

		updatePendingUserStatus: ({ commit }, payload) => {
			commit("UPDATE_PENDING_USER_STATUS", payload);
		},

		resendInvitation: ({ rootState }, payload) => {
			return Vue.prototype.$apiService.post(`accounts/${rootState.User.account.id}/invite/${payload}`);
		},

		deleteInvitation: ({rootState}, payload) => {
			return Vue.prototype.$apiService.delete(`accounts/${rootState.User.account.id}/invitations/${payload}`);
		},

		leave: ({ rootState }) => {
			return Vue.prototype.$apiService.delete(`accounts/${rootState.User.account.id}/leave`);
		},
	},
	getters: {
		membersWithoutMe: (state, getter, rootState) => {
			if (state.members) return state.members.filter(member => member.id !== rootState.User.account.id);
		},
		user: state => id => {
			return state.users.find(user => user.id == id);
		},
		member: state => id => {
			return state.members.find(member => member.id == id);
		},

		selectedUser: state => {
			return state.selectedUser;
		},

		acceptedUsers: state => {
			return state.users.filter(user => user.accepted && user.role_id !== 7);
		},

		acceptedGuests: state => {
			return state.users.filter(user => user.accepted && user.role_id == 7);
		},

		activeUsers: (state, getters) => {
			return getters.acceptedUsers.filter(user => user.role_id !== 7);
		},

		activeGuests: (state, getters) => {
			return getters.acceptedGuests.filter(user => user.role_id == 7);
		},

		activeUsersAndGuests: state => {
			return state.users.filter(user => user.accepted);
		},

		pendingGuests: state => {
			return state.pendingUsers.filter(user => user.role_id == 7);
		},

		pendingUsersWithoutGuests: state => {
			return state.pendingUsers.filter(user => user.role_id !== 7);
		},

		memberAccountByUserId: state => user_id => {
			if (state.members) {
				return Object.values(state.members).find(user => user.id == user_id);
			}
		},

		accountByUserId: state => user_id => {
			return state.users.find(user => user.user.id == user_id);
		},

		isUserOnline: state => user_id => {
			return state.usersOnline[user_id];
		},

		isProductPurchased: state => product_id => {
			return state.organization.purchases.find(purchase => purchase.product.id == product_id);
		}
	},
};

export default Organizations;
